import { useEffect, useRef, useState } from 'react';

import getConfig from 'next/config';
import { useRouter } from 'next/router';

import { Flexbox, IconButton, Text } from '@morressier/ts';

import axios from 'axios';
import { FaDownload } from 'react-icons/fa';
import useSWR from 'swr';

import { TranscriptionResponse } from 'api/transcriptions';
import { useVideoTime } from 'components/VideoPlayer/hooks';
import { secondsToTime } from 'utils/dates';
import { scrollToChild } from 'utils/layout';

import Accordion from './Accordion';
import { getCurrentTranscriptionIndex } from './helpers';
import { StyledTranscriptBox, StyledTranscripts, StyledTranscriptsHeader } from './style';

const { publicRuntimeConfig } = getConfig();

export type Transcription = {
  transcript: string;
  resultStartTime: number;
  resultEndTime: number;
};

export const VideoTranscription = (props: {
  videoElement: HTMLVideoElement | null | undefined;
  organizationId: string;
}) => {
  const { query } = useRouter();
  const [transcriptions, setTranscriptions] = useState<Transcription[]>([]);
  const [highlightIndex, setHighlightIndex] = useState<number>(0);
  const transcriptionRefs = useRef<(HTMLDivElement | null)[]>([]);
  const transcriptionsParentRef = useRef(null);
  const videoTimeCallback = time => {
    const currentTranscrtiptionIndex = getCurrentTranscriptionIndex(transcriptions, time);
    setHighlightIndex(currentTranscrtiptionIndex);
  };
  useVideoTime(props.videoElement, videoTimeCallback);
  const { repo_api } = publicRuntimeConfig;
  const TranscriptionUrl = `${repo_api}/organizations/${props.organizationId}/events/${query.eventId}/documents/${query.article_id}/transcription`;
  const PDFUrl = `${TranscriptionUrl}?pdf=true`;
  const { data } = useSWR<TranscriptionResponse>(TranscriptionUrl, url =>
    axios.get(url).then(res => res.data),
  );

  useEffect(() => {
    const currentTranscriptionRef = transcriptionRefs.current[highlightIndex];
    if (currentTranscriptionRef && currentTranscriptionRef !== null) {
      scrollToChild(transcriptionsParentRef.current, currentTranscriptionRef);
    }
  }, [highlightIndex]);

  useEffect(() => {
    if (data) {
      const parsedTranscription = data.results.map(transcription => ({
        transcript: transcription.alternatives[0].transcript,
        resultStartTime: transcription.alternatives[0].start_time,
        resultEndTime: transcription.alternatives[0].end_time,
      }));

      setTranscriptions(parsedTranscription);
    }
  }, [data]);

  return transcriptions.length ? (
    <Flexbox className="mt1">
      <Accordion
        ariaTitle="transcription"
        header={
          <StyledTranscriptsHeader>
            <Flexbox flexDirection="row" justifyContent="space-between" alignItems="center">
              <Text fontWeight="semiBold" size="h5_new">
                Transcript
              </Text>
              {PDFUrl && (
                <a href={PDFUrl} target="_blank" rel="noreferrer">
                  <IconButton color="secondaryBrand" aria-label="download-transcription">
                    <FaDownload />
                  </IconButton>
                </a>
              )}
            </Flexbox>
          </StyledTranscriptsHeader>
        }
      >
        <StyledTranscripts ref={transcriptionsParentRef}>
          {transcriptions &&
            transcriptions.map((transcription, i) => {
              const transciptKey = `transcript-${i}`;
              return (
                <Flexbox
                  key={transciptKey}
                  flexDirection="row"
                  ref={el => (transcriptionRefs.current[i] = el)}
                  style={{ padding: '0.5rem' }}
                >
                  <Text color="primaryLink" fontWeight="semiBold">
                    {secondsToTime(transcription.resultStartTime)}
                  </Text>
                  <StyledTranscriptBox highlight={highlightIndex === i}>
                    <Text fontWeight={highlightIndex === i ? 'semiBold' : 'regular'}>
                      {transcription.transcript}
                    </Text>
                  </StyledTranscriptBox>
                </Flexbox>
              );
            })}
        </StyledTranscripts>
      </Accordion>
    </Flexbox>
  ) : null;
};
